import { Card, CardContent, CardHeader } from '@/components/ui/shadcn/card'
import { PlusIcon, SparklesIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import { trpc } from '@/config/trpc'
import { useNavigate } from 'react-router'
import { MoodboardStatusBadge } from './components/moodboard-status-badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog'
import { UpgradeModal } from '@/components/upgrade-modal'

// Moodboard list page — see backend schema/moodboard.ts.

export function MoodboardListPage() {
  const navigate = useNavigate()
  const { data: personas, isLoading, refetch } = trpc.moodboard.getAll.useQuery()
  const { mutateAsync: create, isPending: creating } = trpc.moodboard.create.useMutation()
  const { mutateAsync: del } = trpc.moodboard.delete.useMutation()

  // The moodboard queued for deletion drives the confirmation dialog. null = closed.
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const handleCreate = async () => {
    const result = await create({ name: 'Untitled moodboard', items: [] })
    if (!result.ok) {
      setShowUpgradeModal(true)
      return
    }
    await refetch()
    navigate(`/moodboards/${result.moodboard.id}`)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await del({ id: deleteTarget.id })
      await refetch()
      setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <section className="mt-[3vh] flex w-full flex-1 flex-col items-center p-6">
      <div className="text-foreground mt-6 flex w-full max-w-[790px] flex-col gap-2">
        <div className="mb-4">
          <h1 className="text-2xl font-bold">Moodboards</h1>
          <p className="text-muted-foreground text-base">
            Your brand's voice and style, from a pile of inputs. Drop in notes, instructions, links,
            colors, and files, and the analyzer turns the pile into a brand brief that feeds post
            generation. Once analyzed, a moodboard can be picked when writing a post or scheduling
            automations.
          </p>
        </div>

        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <Card
            className={`hover:bg-hover cursor-pointer p-4 transition-transform hover:scale-[1.02] ${creating ? 'opacity-50' : ''}`}
            onClick={creating ? undefined : handleCreate}>
            <CardHeader>
              <span className="bg-primary flex h-[50px] w-[50px] items-center justify-center rounded-full">
                <PlusIcon className="text-primary-foreground" size="28" />
              </span>
            </CardHeader>
            <CardContent className="mt-6 flex flex-col">
              <p className="text-base font-bold">Create new moodboard</p>
              <p className="text-muted-foreground text-sm">Start a fresh context</p>
            </CardContent>
          </Card>

          {personas
            ?.slice()
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
            .map((persona) => {
              const itemCount = persona.items.length
              return (
                <Card
                  key={persona.id}
                  className="hover:bg-hover group relative cursor-pointer p-4 transition-transform hover:scale-[1.02]"
                  onClick={() => navigate(`/moodboards/${persona.id}`)}>
                  <CardHeader className="flex flex-row items-start justify-between">
                    <span className="bg-accent flex h-[50px] w-[50px] items-center justify-center rounded-full">
                      <SparklesIcon className="text-accent-foreground" size="22" />
                    </span>
                    <MoodboardStatusBadge status={persona.status} />
                  </CardHeader>
                  <CardContent className="mt-6 flex flex-col">
                    <p className="truncate text-base font-bold">{persona.name}</p>
                    <p className="text-muted-foreground text-sm">
                      {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </p>
                  </CardContent>
                  <p className="text-muted-foreground text-xs">
                    Updated {new Date(persona.updatedAt).toLocaleDateString()}
                  </p>
                  <button
                    type="button"
                    aria-label={`Delete ${persona.name}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteTarget({ id: persona.id, name: persona.name })
                    }}
                    className="text-muted-foreground hover:bg-hover hover:text-destructive absolute bottom-2 right-2 rounded-md p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <TrashIcon size={16} />
                  </button>
                </Card>
              )
            })}

          {isLoading && (
            <Card className="animate-pulse p-4">
              <CardHeader>
                <span className="bg-muted flex h-[50px] w-[50px] items-center justify-center rounded-full" />
              </CardHeader>
              <CardContent className="mt-8 flex flex-col">
                <div className="bg-muted mb-2 h-4 rounded-sm" />
                <div className="bg-muted h-3 rounded-sm" />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null)
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete moodboard?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes "{deleteTarget?.name}" and all of its items. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                // Keep the dialog open through the async delete (shows "Deleting…");
                // confirmDelete closes it on success.
                e.preventDefault()
                void confirmDelete()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="moodboards"
        surface="moodboard_list"
      />
    </section>
  )
}
